import axios from "axios";
const chatbot = "/bp/api/chatbot";
const bp = "/bp";
// Helper function to get auth headers
const getAuthHeaders = (token, isFormData = false) => {
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }

  return { headers };
};

export const createSession = async (token, moduleId = null) => {
  try {
    const requestBody = {};
    if (moduleId) {
      requestBody.module_id = moduleId;
    }

    const response = await axios.post(
      `${chatbot}/session`,
      requestBody,
      getAuthHeaders(token)
    );
    return response.data.session_id;
  } catch (error) {
    throw new Error("Failed to create a new session");
  }
};

export const getSessions = async (token, page = 1, moduleId = null) => {
  try {
    const params = new URLSearchParams({
      page_number: page.toString(),
    });

    if (moduleId) {
      params.append("module_id", moduleId.toString());
    }

    const response = await axios.get(
      `${chatbot}/session?${params.toString()}`,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to load sessions");
  }
};

export const getRecipeList = async (token) => {
  try {
    const response = await axios.get(
      `${bp}/get_recipe_list`,
      getAuthHeaders(token)
    );
    return response.data.data || [];
  } catch (error) {
    throw new Error("Failed to load recipes");
  }
};

export const getmodule = async (token) => {
  try {
    const response = await axios.get(
      `${chatbot}/module`,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to load modules");
  }
};

export const getMessages = async (token, sessionId) => {
  try {
    const response = await axios.get(
      `${chatbot}/chat?session_id=${sessionId}`,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to load messages");
  }
};

export const sendMessage = async (token, data) => {
  try {
    const response = await axios.post(
      `${chatbot}/chat`,
      data,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to send message");
  }
};

export const addModule = async (token, data) => {
  const response = await axios.post(
    `${chatbot}/module`,
    data,
    getAuthHeaders(token, true)
  );
  return response.data;
};

export const deleteModule = async (token, data) => {
  try {
    const response = await axios.delete(
      `${chatbot}/module/${data.id}`,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to delete module");
  }
};

export const deleteSession = async (token, data) => {
  try {
    const response = await axios.delete(
      `${chatbot}/session/${data.id}`,
      getAuthHeaders(token)
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to delete session");
  }
};

export const sendStreamingMessage = async (token, messageData, onChunk) => {
  try {
    const response = await fetch(`${chatbot}/async_chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(messageData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error("ReadableStream not yet supported in this browser.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    // A single reader.read() has no relationship to one SSE event — it can
    // land mid-line (a "data: {\"con" / "tent\": ...}" split) or contain
    // several whole events back to back. Buffer everything and only act on
    // complete "\n\n"-terminated events, or the raw "data: ..." frames end up
    // rendered verbatim in the chat instead of the text they carry.
    let buffer = "";
    let accumulatedContent = "";
    let finished = false;

    while (!finished) {
      const { value, done: doneReading } = await reader.read();
      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      let eventEnd;
      while ((eventEnd = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, eventEnd);
        buffer = buffer.slice(eventEnd + 2);

        const jsonData = rawEvent
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("\n");
        if (!jsonData) continue;

        if (jsonData === "[DONE]") {
          finished = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonData);
          const content = parsed.content ?? parsed.message ?? parsed.text ?? "";
          accumulatedContent += content;
          if (onChunk) onChunk(accumulatedContent);
        } catch {
          // Not a complete/valid JSON payload — drop it rather than
          // splatting the raw SSE frame into the visible chat.
        }
      }

      if (doneReading) finished = true;
    }

    return accumulatedContent;
  } catch (error) {
    throw new Error(`Failed to send streaming message: ${error.message}`);
  }
};
